// src/components/Login.js
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';

const themes = {
  dark: {
    bgPrimary: 'bg-neutral-950',
    bgSecondary: 'bg-neutral-900',
    bgTertiary: 'bg-neutral-800',
    border: 'border-neutral-800',
    textPrimary: 'text-neutral-100',
    textSecondary: 'text-neutral-300',
    textMuted: 'text-neutral-400',
    accent: 'bg-neutral-700 hover:bg-neutral-600',
    inputBg: 'bg-neutral-800',
    inputBorder: 'border-neutral-700',
    ring: 'focus:ring-2 focus:ring-neutral-600 focus:outline-none'
  },
  slate: {
    bgPrimary: 'bg-slate-50',
    bgSecondary: 'bg-white',
    bgTertiary: 'bg-slate-100',
    border: 'border-slate-200',
    textPrimary: 'text-slate-900',
    textSecondary: 'text-slate-700',
    textMuted: 'text-slate-500',
    accent: 'bg-slate-900 hover:bg-slate-800',
    inputBg: 'bg-white',
    inputBorder: 'border-slate-300',
    ring: 'focus:ring-2 focus:ring-slate-300 focus:outline-none'
  },
  bone: {
    bgPrimary: 'bg-stone-50',
    bgSecondary: 'bg-white',
    bgTertiary: 'bg-stone-100',
    border: 'border-stone-200',
    textPrimary: 'text-stone-900',
    textSecondary: 'text-stone-700',
    textMuted: 'text-stone-500',
    accent: 'bg-stone-900 hover:bg-stone-800',
    inputBg: 'bg-white',
    inputBorder: 'border-stone-300',
    ring: 'focus:ring-2 focus:ring-stone-300 focus:outline-none'
  }
};

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPassword, setShow] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('dark');

  const { login, currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) navigate('/dashboard');
  }, [currentUser, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem('netfly-theme');
    if (saved && themes[saved]) setCurrentTheme(saved);
  }, []);

  const t = useMemo(() => themes[currentTheme] ?? themes.dark, [currentTheme]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Por favor, completa todos los campos');
      return;
    }
    try {
      setError('');
      setLoading(true);
      const result = await login(username, password);
      if (result.success) navigate('/dashboard');
      else setError(result.message);
    } catch {
      setError('Error al iniciar sesión. Por favor, intenta nuevamente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${t.bgPrimary} flex items-center justify-center px-4`}>
      <div className={`w-full max-w-md rounded-2xl border ${t.border} ${t.bgSecondary} shadow-sm`}>
        {/* Encabezado centrado con logo en círculo */}
        <div className="px-6 pt-8 pb-4">
        <div className="flex justify-center">
          <div className={`w-16 h-16 rounded-full ${t.bgTertiary} border ${t.border} flex items-center justify-center overflow-hidden`}>
            <img
              src="/images/thend-logo.png"   // ✅ ruta correcta
              alt="thend"
              className="h-full w-full object-contain"  // ✅ ahora ocupa todo el círculo
              style={{ objectPosition: 'center' }}
            />
          </div>
        </div>
          <h1 className={`mt-4 text-2xl font-semibold text-center ${t.textPrimary}`}>Bienvenido</h1>
          {/* Se eliminó el subtítulo "Inicia sesión para acceder al panel." */}
        </div>

        <div className={`h-px w-full ${currentTheme === 'dark' ? 'bg-neutral-800' : currentTheme === 'slate' ? 'bg-slate-200' : 'bg-stone-200'}`} />

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className={`rounded-lg border ${t.border} ${t.bgTertiary} p-3 flex items-start`}>
              <AlertCircle className={`${currentTheme === 'dark' ? 'text-red-400' : 'text-red-600'} mr-2 mt-0.5`} size={18} />
              <p className={`text-sm ${currentTheme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
            </div>
          )}

          <label className="block">
            <span className={`block text-xs mb-1.5 ${t.textSecondary}`}>Usuario</span>
            <div className={`flex items-center rounded-lg border ${t.inputBorder} ${t.bgSecondary}`}>
              <div className={`px-3 py-2 ${t.bgTertiary} rounded-l-lg border-r ${t.border}`}>
                <User size={18} className={t.textMuted} />
              </div>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={`w-full px-3 py-2 ${t.inputBg} ${t.textPrimary} placeholder:${t.textMuted} rounded-r-lg ${t.ring}`}
                placeholder="tu_usuario"
                autoComplete="username"
              />
            </div>
          </label>

          <label className="block">
            <span className={`block text-xs mb-1.5 ${t.textSecondary}`}>Contraseña</span>
            <div className={`relative flex items-center rounded-lg border ${t.inputBorder} ${t.bgSecondary}`}>
              <div className={`px-3 py-2 ${t.bgTertiary} rounded-l-lg border-r ${t.border}`}>
                <Lock size={18} className={t.textMuted} />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-3 py-2 ${t.inputBg} ${t.textPrimary} placeholder:${t.textMuted} rounded-r-lg pr-10 ${t.ring}`}
                placeholder="********"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 inline-flex items-center justify-center"
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? <EyeOff size={18} className={t.textMuted} /> : <Eye size={18} className={t.textMuted} />}
              </button>
            </div>
          </label>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2.5 rounded-lg text-white font-medium transition-colors border border-transparent ${t.accent}`}
          >
            {loading ? 'Iniciando…' : 'Entrar'}
          </button>

          <p className={`text-xs text-center mt-2 ${t.textMuted}`}>
            ¿Olvidaste tu contraseña? Contacta al admin.
          </p>
        </form>
      </div>
    </div>
  );
};

export default Login;
